import { useEffect, useState } from "react";
import { Form, Switch, Select } from "antd";
import { ApiOutlined } from "@ant-design/icons";
import {
  hidePriceLabels,
  iconMap,
  inputCardOptions,
  pricingTiers,
  toggleCardOptions,
} from "../../../utils/Common";

const groupOptions = (options, inputOptions = [], inputValues = {}) => {
  const groups = {
    Options: [],
    Device: [],
    "Video Integration": [],
    "Mobile Credential": [],
  };

  // Check if Mobile Credential has value > 0
  const mobileCredentialValue = inputValues["Invitation"] || 0;
  const showMobileCredentialCards = mobileCredentialValue > 0;

  // Check if Channel has value > 0
  const channelValue = inputValues["Channel"] || 0;
  const showVideoIntegrationCards = channelValue > 0;

  // Mobile credential related card names
  const mobileCredentialCards = [
    "Sicunet Mobile",
    "Wavelynx Mobile",
    "Farpointe Mobile",
  ];

  // Video integration card names
  const videoIntegrationCards = [
    "Vitek NVR",
    "Speco NVR",
    "DW Spectrum",
    "Thumb Drive NVR",
  ];

  // Options group card names
  const optionsCards = [
    "REST API",
    "Log API",
    "Credential Invite",
    "Visitor Invite",
    "Gemini",
    "Mobile App",
    "Single Sign On",
    "Badge",
  ];

  // Process toggle options
  options?.forEach((option) => {
    // Handle mobile credential cards
    if (mobileCredentialCards.includes(option.label)) {
      // Always hide mobile credential child cards (Sicunet/Wavelynx/Farpointe)
      // from the UI. We'll still keep them enabled in state when
      // "Mobile Credential" has Invitation > 0.
      return;
    }

    // Handle video integration cards
    if (videoIntegrationCards.includes(option.label)) {
      if (showVideoIntegrationCards) {
        groups["Video Integration"].push({ ...option, type: "toggle" });
      }
      return;
    }

    // Group Options cards
    if (optionsCards.includes(option.label)) {
      groups.Options.push({ ...option, type: "toggle" });
    }
    // Everything else goes to Options
    else {
      groups.Options.push({ ...option, type: "toggle" });
    }
  });

  // Process input options
  inputOptions.forEach((option) => {
    const item = { ...option, type: "input" };

    if (option.label === "Channel") {
      groups["Video Integration"].unshift(item);
    } else if (
      [
        "Lockset",
        "Allegion Lockset",
        // "Facegate",
        "OSDP Door",
        "OSDP Device",
        "Trilogy Lockset",
        "Unit",
      ].includes(option.label)
    ) {
      groups.Device.push(item);
    } else if (
      option.label === "Mobile Credential" ||
      option.label === "Landline"
    ) {
      // Put Landline + Mobile Credential beside Device (same group/layout)
      groups.Device.push(item);
    }
  });

  // Remove empty groups
  return Object.entries(groups).filter(([_, items]) => items.length > 0);
};

const OptionCard = ({ option, selected, onToggle, disabled, price = 0 }) => {
  const icon = iconMap[option.label] || (
    <ApiOutlined style={{ fontSize: "16px" }} />
  );

  const shouldHidePrice = hidePriceLabels.includes(option.label);

  return (
    <div
      className={`relative border rounded-[4px] p-2 transition-all ${
        selected
          ? "border-customColor bg-customColor_10"
          : `border-gray-300 bg-white ${disabled ? "" : "hover:border-gray-400"}`
      } ${disabled ? "opacity-60  bg-gray-50" : "cursor-pointer"}`}
      style={{
        borderWidth: "1.5px",
        height: "72px",
      }}
      onClick={() => !disabled && onToggle()}
    >
      {/* Icon and name */}
      <div
        className="flex items-start gap-2 mb-1"
        style={{ minHeight: "32px" }}
      >
        <div className="text-gray-600 flex-shrink-0">{icon}</div>
        <div className="flex-1 text-xs font-medium text-gray-800 line-clamp-2">
          {option.label}
        </div>
      </div>

      {/* Footer with toggle */}
      <div
        className={`flex items-center ${shouldHidePrice ? "justify-end" : "justify-between"} mt-auto`}
      >
        {!shouldHidePrice && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">$</span>
            <span className="text-xs font-medium text-gray-900">
              {selected ? price.toFixed(2) : "0.00"}
            </span>
          </div>
        )}

        <div className={selected ? "" : "ml-auto"}>
          <Switch
            checked={selected}
            onChange={(checked, e) => {
              e.stopPropagation();
              onToggle();
            }}
            disabled={disabled}
            size="small"
            style={{
              backgroundColor: selected ? undefined : "#d9d9d9",
            }}
          />
        </div>
      </div>
    </div>
  );
};

const InputCard = ({
  option,
  value = 0,
  onChange,
  disabled,
  max = 255,
  min = 0,
  step = 1,
  prefix = "$",
  price = 0,
  valueInputClassName,
  readOnly = false,
  badge,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  const icon = iconMap[option.label] || (
    <ApiOutlined style={{ fontSize: "16px" }} />
  );
  const isInteractive = !disabled && !readOnly;
  const shouldHidePrice = hidePriceLabels.includes(option.label);

  const handleIncrement = (e) => {
    e.stopPropagation();
    if (isInteractive && value < max) {
      onChange(value + step);
    }
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    if (isInteractive && value > min) {
      onChange(value - step);
    }
  };

  const handleInputChange = (e) => {
    if (!isInteractive) return;
    const newValue = e.target.value;
    if (newValue === "" || newValue === "-") {
      setTempValue(newValue);
      return;
    }
    const numValue = parseInt(newValue);
    if (!isNaN(numValue)) {
      setTempValue(numValue);
    }
  };

  const handleInputBlur = () => {
    if (!isInteractive) return;
    setIsEditing(false);
    let finalValue =
      typeof tempValue === "number" ? tempValue : parseInt(tempValue) || min;

    if (finalValue > max) {
      finalValue = max;
    } else if (finalValue < min) {
      finalValue = min;
    }

    onChange(finalValue);
    setTempValue(finalValue);
  };

  const handleInputFocus = () => {
    if (!isInteractive) return;
    setIsEditing(true);
    setTempValue(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      handleIncrement(e);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleDecrement(e);
    }
  };

  useEffect(() => {
    if (!isEditing) {
      setTempValue(value);
    }
  }, [value, isEditing]);

  return (
    <div
      className={`relative border rounded-[4px] p-2 transition-all ${
        value > 0
          ? "border-customColor bg-customColor_10"
          : `border-gray-300 bg-white ${disabled || readOnly ? "" : "hover:border-gray-400"}`
      } ${disabled ? "opacity-60 bg-gray-50" : readOnly ? "bg-gray-50" : "cursor-pointer"}`}
      style={{
        borderWidth: "1.5px",
        height: "72px",
      }}
    >
      {badge ? (
        <span className="absolute -top-1.5 right-1 z-10 rounded px-1 py-[1px] text-[9px] leading-tight font-medium text-white bg-customColor whitespace-nowrap">
          {badge}
        </span>
      ) : null}

      {/* Icon and name */}
      <div
        className="flex items-start gap-2 mb-1"
        style={{ minHeight: "32px" }}
      >
        <div className="text-gray-600 flex-shrink-0">{icon}</div>
        <div className="flex-1 text-xs font-medium text-gray-800 line-clamp-2">
          {option.label}
        </div>
      </div>

      {/* Footer with price display and horizontal increment/decrement controls */}
      <div
        className={`flex items-center mt-auto gap-1 ${
          shouldHidePrice ? "justify-end" : "justify-between"
        }`}
      >
        {!shouldHidePrice && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">{prefix}</span>
            <span className="text-xs font-medium text-gray-900">
              {price.toFixed(2)}{" "}
            </span>
          </div>
        )}

        {/* Horizontal Increment/Decrement Controls */}
        <div className="flex items-center">
          {/* Decrement button */}
          <button
            type="button"
            onClick={handleDecrement}
            disabled={!isInteractive || value <= min}
            className="w-3 h-3 flex items-center justify-center text-gray-600 hover:text-customColor disabled:text-gray-300 disabled: transition-colors rounded"
            style={{ padding: 0, border: "none", background: "none" }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M3 6L7 3V9L3 6Z" />
            </svg>
          </button>

          {/* Editable value input */}
          <input
            type="text"
            value={isEditing ? tempValue : value}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={!isInteractive}
            readOnly={readOnly}
            className={`${valueInputClassName || "w-10"} h-5 text-center text-xs font-medium text-gray-900 border border-gray-300 rounded focus:outline-none focus:border-customColor disabled:bg-gray-100 disabled:`}
            style={{ padding: "0 2px" }}
          />

          {/* Increment button */}
          <button
            type="button"
            onClick={handleIncrement}
            disabled={!isInteractive || value >= max}
            className="w-3 h-3 flex items-center justify-center text-gray-600 hover:text-customColor disabled:text-gray-300 disabled: transition-colors rounded"
            style={{ padding: 0, border: "none", background: "none" }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <path d="M9 6L5 9V3L9 6Z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Updated SelectInputCard component
const SelectInputCard = ({
  option,
  value = 0,
  onChange,
  disabled,
  max = 25500,
  prefix = "$",
  price = 0,
  selectOptions: customSelectOptions,
  readOnly = false,
  badge,
  selectWidth = 70,
  forceShowPrice = false,
  valueOnly = false,
}) => {
  const icon = iconMap[option.label] || (
    <ApiOutlined style={{ fontSize: "16px" }} />
  );
  const isInteractive = !disabled && !readOnly;
  const shouldHidePrice =
    valueOnly || (!forceShowPrice && hidePriceLabels.includes(option.label));
  const numericPrice = Number(price);
  const displayPrice = Number.isFinite(numericPrice) ? numericPrice : 0;

  // Custom options (e.g. Term) or generate: 0, 100, 200, ... max
  const selectOptions =
    customSelectOptions?.length > 0
      ? customSelectOptions
      : Array.from({ length: Math.floor(max / 100) + 1 }, (_, i) => {
          const optionValue = i * 100;
          return { value: optionValue, label: `${optionValue}` };
        });

  return (
    <div
      className={`relative border rounded-[4px] p-2 transition-all ${
        value > 0
          ? "border-customColor bg-customColor_10"
          : `border-gray-300 bg-white ${disabled || readOnly ? "" : "hover:border-gray-400"}`
      } ${disabled ? "opacity-60 bg-gray-50" : readOnly ? "bg-gray-50" : "cursor-pointer"}`}
      style={{
        borderWidth: "1.5px",
        height: "72px",
      }}
    >
      {badge ? (
        <span className="absolute -top-1.5 right-1 z-10 rounded px-1 py-[1px] text-[9px] leading-tight font-medium text-white bg-customColor whitespace-nowrap">
          {badge}
        </span>
      ) : null}

      {/* Icon and name */}
      <div
        className="flex items-start gap-2 mb-1"
        style={{ minHeight: "32px" }}
      >
        <div className="text-gray-600 flex-shrink-0">{icon}</div>
        <div className="flex-1 text-xs font-medium text-gray-800 line-clamp-2">
          {option.label}
        </div>
      </div>

      {/* Footer — value only (left, Lockset-style control), or price + select */}
      <div
        className={`flex items-center mt-auto gap-1 ${
          valueOnly
            ? "justify-start"
            : shouldHidePrice
              ? "justify-end"
              : "justify-between"
        }`}
      >
        {valueOnly ? (
          <div className="flex items-center">
            <button
              type="button"
              disabled
              className="w-3 h-3 flex items-center justify-center text-gray-300 rounded"
              style={{ padding: 0, border: "none", background: "none" }}
              tabIndex={-1}
              aria-hidden
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="currentColor"
              >
                <path d="M3 6L7 3V9L3 6Z" />
              </svg>
            </button>
            <input
              type="text"
              value={value > 0 ? value : ""}
              readOnly
              disabled
              className="w-10 h-5 text-center text-xs font-medium text-gray-900 border border-gray-300 rounded bg-gray-100 disabled:bg-gray-100"
              style={{ padding: "0 2px" }}
              placeholder="—"
            />
            <button
              type="button"
              disabled
              className="w-3 h-3 flex items-center justify-center text-gray-300 rounded"
              style={{ padding: 0, border: "none", background: "none" }}
              tabIndex={-1}
              aria-hidden
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="currentColor"
              >
                <path d="M9 6L5 9V3L9 6Z" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            {!shouldHidePrice && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">{prefix}</span>
                <span className="text-xs font-medium text-gray-900">
                  {displayPrice.toFixed(2)}{" "}
                </span>
              </div>
            )}

            <Select
              value={
                customSelectOptions?.length > 0 ? value || undefined : value
              }
              placeholder={customSelectOptions?.length > 0 ? "—" : undefined}
              onChange={onChange}
              disabled={!isInteractive}
              size="small"
              style={
                selectWidth === "auto"
                  ? { width: "auto", minWidth: 56 }
                  : { width: selectWidth }
              }
              showSearch
              optionFilterProp="label"
              className={`select-as-input${
                selectWidth === "auto"
                  ? " !w-auto [&_.ant-select-selector]:!w-auto"
                  : ""
              }`}
              options={selectOptions}
              popupMatchSelectWidth={false}
              allowClear={isInteractive && customSelectOptions?.length > 0}
            />
          </>
        )}
      </div>
    </div>
  );
};

const OptionCards = ({
  name,
  options,
  inputOptions = inputCardOptions,
  disabled = false,
  loading = false,
  codesData,
  type,
  // New unified state prop
  value = {},
  onChange,
}) => {
  // Use internal state as fallback if props not provided
  const [internalValue, setInternalValue] = useState({});

  // Determine whether to use external or internal state
  const useExternalState = onChange !== undefined;

  const currentValue = useExternalState ? value : internalValue;

  // Add this useEffect after your state declarations
  useEffect(() => {
    if (Object.keys(currentValue).length > 0) {
      const updatedValue = {};

      Object.entries(currentValue).forEach(([key, val]) => {
        if (val.type === "toggle" && val.enabled) {
          updatedValue[key] = {
            ...val,
            price: getPrice(key, 1, val.itemNo), // Pass stored itemNo
          };
        } else if (val.type === "input" || val.type === "select") {
          // Landline price comes from IntercomPrice (API), not license codes
          if (val.itemNo === 207 || key === "Landline") {
            updatedValue[key] = { ...val };
          } else {
            updatedValue[key] = {
              ...val,
              price: getPrice(key, val.quantity, val.itemNo), // Pass stored itemNo
            };
          }
        }
      });

      // Only update if prices actually changed
      const pricesChanged = Object.keys(updatedValue).some(
        (key) => updatedValue[key].price !== currentValue[key]?.price,
      );

      if (pricesChanged) {
        if (useExternalState) {
          onChange(updatedValue);
        } else {
          setInternalValue(updatedValue);
        }
      }
    }
  }, [type, codesData]);

  const getItemNoByLabel = (label) => {
    // First check in toggleCardOptions
    const toggleOption = toggleCardOptions.find((item) => item.name === label);
    if (toggleOption) return toggleOption.value;

    // Then check in inputCardOptions
    const inputOption = inputCardOptions.find((item) => item.label === label);
    if (inputOption) return inputOption.itemNo;

    return null;
  };

  const getPriceByItemNo = (itemNo, tierCode = null) => {
    if (!codesData?.data) {
      return 0;
    }

    let codeItem;

    if (tierCode) {
      const baseItem = codesData.data.find((item) => item.Item === itemNo);

      if (baseItem) {
        const constructedCodeName = `${baseItem.CodeName}-${tierCode}`;

        codeItem = codesData.data.find(
          (item) =>
            item.Item === itemNo && item.CodeName === constructedCodeName,
        );
      }

      // If not found with constructed name, try matching by Item and Code directly
      if (!codeItem) {
        codeItem = codesData.data.find(
          (item) => item.Item === itemNo && item.Code === tierCode,
        );
      }
    } else {
      // For non-tiered items (toggles), just match by Item
      codeItem = codesData.data.find((item) => item.Item === itemNo);
    }

    if (!codeItem) {
      return 0;
    }

    const price =
      type === 0 ? parseFloat(codeItem.Monthly) : parseFloat(codeItem.Lifetime);

    return price || 0;
  };

  const getPrice = (itemLabel, quantity = 1, itemNo = null) => {
    // Use itemNo if provided
    const lookupKey = itemNo || itemLabel;

    if (pricingTiers[lookupKey]) {
      const tiers = pricingTiers[lookupKey];
      let totalPrice = 0;
      let remainingQuantity = quantity;
      let previousMax = 0;

      // Check if this is a divided-by-100 item using itemNo
      const isDividedBy100 = itemNo === 209; // Mobile Credential (UI hundredths)

      for (const tier of tiers) {
        if (remainingQuantity <= 0) break;

        const tierCapacity = tier.max - previousMax;
        const unitsInThisTier = Math.min(remainingQuantity, tierCapacity);

        // Pass itemNo and tier.code (not tier.codeName)
        const tierPrice = getPriceByItemNo(itemNo, tier.code);

        if (isDividedBy100) {
          totalPrice += (unitsInThisTier / 100) * tierPrice;
        } else {
          totalPrice += unitsInThisTier * tierPrice;
        }

        remainingQuantity -= unitsInThisTier;
        previousMax = tier.max;
      }

      return totalPrice;
    } else {
      // For non-tiered items (like toggles), just pass the itemNo without tier code
      return getPriceByItemNo(itemNo || itemLabel);
    }
  };

  // Extract inputValues for grouping logic (for Mobile Credential visibility)
  const inputValues = Object.entries(currentValue).reduce((acc, [key, val]) => {
    if (val.type === "input" || val.type === "select") {
      // Use fieldName if available, otherwise use the key
      const fieldKey = val.fieldName || key;
      acc[fieldKey] = val.quantity ?? 0;
    }
    return acc;
  }, {});

  // Regroup options whenever inputValues changes
  const groupedOptions = groupOptions(
    options?.filter((item) => item.label !== "Expired"),
    inputOptions,
    inputValues,
  );

  // Mobile Credential children are always hidden from the UI.
  // If Invitation > 0, ensure child toggles are enabled in state so
  // backend payloads stay correct.
  useEffect(() => {
    const invitationValue = inputValues["Invitation"] || 0;
    const shouldEnableChildren = invitationValue > 0;

    const childLabels = [
      "Sicunet Mobile",
      "Wavelynx Mobile",
      "Farpointe Mobile",
    ];

    let nextValue = { ...currentValue };
    let changed = false;

    childLabels.forEach((label) => {
      const opt = options?.find((o) => o.label === label);
      const itemNo = getItemNoByLabel(label);
      const targetPrice = getPrice(label, 1, itemNo);

      if (shouldEnableChildren) {
        const existing = nextValue[label];
        const needsUpdate =
          !existing ||
          existing.enabled !== true ||
          existing.value !== opt?.value ||
          existing.itemNo !== itemNo ||
          existing.price !== targetPrice;

        if (needsUpdate) {
          nextValue[label] = {
            type: "toggle",
            enabled: true,
            value: opt?.value,
            itemNo,
            price: targetPrice,
          };
          changed = true;
        }
      } else if (nextValue[label] != null) {
        delete nextValue[label];
        changed = true;
      }
    });

    if (!changed) return;
    if (useExternalState) onChange(nextValue);
    else setInternalValue(nextValue);
  }, [inputValues["Invitation"], type, codesData]);

  const organizeColumns = (groups) => {
    const rows = {
      row1: [], // Options
      rowDevice: [], // Devices
      rowMobile: [], // Mobile Credential
      rowVideo: [], // Video Integration
    };

    groups.forEach(([groupName, groupItems]) => {
      if (groupName === "Options") {
        rows.row1.push([groupName, groupItems]);
      } else if (groupName === "Device") {
        rows.rowDevice.push([groupName, groupItems]);
      } else if (groupName === "Video Integration") {
        rows.rowVideo.push([groupName, groupItems]);
      } else if (groupName === "Mobile Credential") {
        rows.rowMobile.push([groupName, groupItems]);
      }
    });

    return rows;
  };

  const handleToggle = (optionLabel, optionValue, form) => {
    const newValue = { ...currentValue };

    if (newValue[optionLabel]?.enabled) {
      delete newValue[optionLabel];
    } else {
      // Get itemNo from toggleCardOptions
      const itemNo = getItemNoByLabel(optionLabel);

      // Calculate price using itemNo
      const price = getPrice(optionLabel, 1, itemNo);

      newValue[optionLabel] = {
        type: "toggle",
        enabled: true,
        price: price,
        value: optionValue,
        itemNo: itemNo,
      };
    }

    // Update state
    if (useExternalState) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }

    // Update form value
    const selectedValues = Object.entries(newValue)
      .filter(([_, val]) => val.type === "toggle" && val.enabled)
      .map(([key, _]) => {
        const option = options?.find((opt) => opt.label === key);
        return option ? option.value : null;
      })
      .filter((val) => val !== null);
    form?.setFieldsValue({ [name]: selectedValues });
  };

  const handleInputChange = (
    optionLabel,
    fieldName,
    newQuantity,
    cardType,
    form,
    itemNo = null, // Add this parameter
  ) => {
    const newValue = { ...currentValue };
    const option = options?.find((opt) => opt.label === optionLabel);
    const optionValue = option ? option.value : null;

    // Pass itemNo to getPrice
    const price = getPrice(optionLabel, newQuantity, itemNo);
    newValue[optionLabel] = {
      type: cardType,
      quantity: newQuantity,
      price: price,
      fieldName: fieldName,
      value: optionValue,
      itemNo: itemNo, // Store itemNo for future reference
    };

    if (newQuantity === 0) {
      if (fieldName === "Invitation") {
        // Remove all mobile credential cards
        ["Sicunet Mobile", "Wavelynx Mobile", "Farpointe Mobile"].forEach(
          (cardName) => {
            delete newValue[cardName];
          },
        );
      } else if (fieldName === "Channel") {
        // Remove all video integration cards
        ["Vitek NVR", "Speco NVR", "DW Spectrum", "Thumb Drive NVR"].forEach(
          (cardName) => {
            delete newValue[cardName];
          },
        );
      }
    }

    // Update state
    if (useExternalState) {
      onChange(newValue);
    } else {
      setInternalValue(newValue);
    }

    // Update form value with the specific field name
    form?.setFieldsValue({ [fieldName]: newQuantity });
  };

  if (loading) {
    return (
      <div className="px-2 py-3">
        <div className="text-gray-500">Loading options...</div>
      </div>
    );
  }

  const getGridClass = (columnGroups, groupName = "") => {
    // For Options and Device groups, use 8 columns
    if (groupName === "Options" || groupName === "Device") {
      return "grid gap-2 grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8";
    }
    // For Video Integration (Channel + its enabled children), fit them into one row on larger screens.
    if (groupName === "Video Integration") {
      // Use the same column config as Options/Device to keep card width consistent.
      // With <= 5 cards, lg:grid-cols-6 is enough to keep everything on one row.
      return "grid gap-2 grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8";
    }
    // For other groups, use standard 4 column layout
    return "grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
  };

  const renderColumn = (columnGroups, setFieldsValue) => (
    <div className="space-y-4">
      {columnGroups.map(([groupName, groupItems]) => {
        // Check if any mobile credential card is selected
        const isMobileCredSelected = groupItems.some(
          (item) =>
            item.type === "toggle" &&
            ["Sicunet Mobile", "Wavelynx Mobile", "Farpointe Mobile"].includes(
              item.label,
            ) &&
            currentValue[item.label]?.enabled,
        );

        // Check if any video integration card is selected
        const isVideoCardSelected = groupItems.some(
          (item) =>
            item.type === "toggle" &&
            [
              "Vitek NVR",
              "Speco NVR",
              "DW Spectrum",
              "Thumb Drive NVR",
            ].includes(item.label) &&
            currentValue[item.label]?.enabled,
        );

        return (
          <div key={groupName}>
            <h3 className="text-xs font-medium text-gray-900 mb-2">
              {groupName}
            </h3>

            <div className={getGridClass(groupItems, groupName)}>
              {groupItems.map((option) => {
                if (option.type === "input") {
                  const currentQuantity =
                    currentValue[option.label]?.quantity ?? 0;

                  // Get itemNo dynamically
                  const itemNo =
                    option.itemNo || getItemNoByLabel(option.label);

                  const storedPrice = currentValue[option.label]?.price;
                  const currentPrice =
                    itemNo === 207 && storedPrice != null
                      ? Number(storedPrice) || 0
                      : getPrice(option.label, currentQuantity, itemNo);

                  if (option.itemNo === 207) {
                    return (
                      <SelectInputCard
                        key={option.fieldName}
                        option={option}
                        value={currentQuantity}
                        disabled={disabled}
                        readOnly
                        badge={option.badge}
                        valueOnly
                      />
                    );
                  }

                  if (option.itemNo === 209) {
                    return (
                      <SelectInputCard
                        key={option.fieldName}
                        option={option}
                        value={currentQuantity}
                        onChange={(newValue) =>
                          handleInputChange(
                            option.label,
                            option.fieldName,
                            newValue ?? 0,
                            "select",
                            { setFieldsValue },
                            option.itemNo,
                          )
                        }
                        disabled={disabled}
                        readOnly={!!option.readOnly}
                        badge={option.badge}
                        max={option.max || 25500}
                        prefix={option.prefix || "$"}
                        price={currentPrice}
                        selectOptions={option.selectOptions}
                        selectWidth={70}
                      />
                    );
                  }

                  return (
                    <InputCard
                      key={option.fieldName}
                      option={option}
                      value={currentQuantity}
                      onChange={(newValue) =>
                        handleInputChange(
                          option.label,
                          option.fieldName,
                          newValue,
                          "input",
                          { setFieldsValue },
                          option.itemNo,
                        )
                      }
                      disabled={disabled}
                      readOnly={!!option.readOnly}
                      badge={option.badge}
                      max={option.max || 255}
                      min={option.min || 0}
                      step={option.step || 1}
                      prefix={option.prefix || "$"}
                      price={currentPrice}
                      valueInputClassName={
                        option.itemNo === 208 || option.itemNo === 207
                          ? "w-14 min-w-[3.5rem]"
                          : undefined
                      }
                    />
                  );
                } else {
                  const isSelected =
                    currentValue[option.label]?.enabled || false;

                  // Get itemNo from toggleCardOptions
                  const itemNo = getItemNoByLabel(option.label);

                  // Recalculate price based on current type to handle type changes
                  const currentPrice = isSelected
                    ? getPrice(option.label, 1, itemNo)
                    : 0;

                  // Determine if this card should be disabled
                  let isCardDisabled = disabled;

                  // // For Mobile Credential cards
                  // if (
                  //   [
                  //     "Sicunet Mobile",
                  //     "Wavelynx Mobile",
                  //     "Farpointe Mobile",
                  //   ].includes(option.label)
                  // ) {
                  //   // Disable if another mobile cred card is selected and this one is not
                  //   isCardDisabled =
                  //     disabled || (isMobileCredSelected && !isSelected);
                  // }

                  // // For Video Integration cards
                  // if (
                  //   [
                  //     "Vitek NVR",
                  //     "Speco NVR",
                  //     "DW Spectrum",
                  //     "Thumb Drive NVR",
                  //   ].includes(option.label)
                  // ) {
                  //   // Disable if another video card is selected and this one is not
                  //   isCardDisabled =
                  //     disabled || (isVideoCardSelected && !isSelected);
                  // }

                  return (
                    <OptionCard
                      key={option.value}
                      option={option}
                      selected={isSelected}
                      onToggle={() =>
                        handleToggle(option.label, option.value, {
                          setFieldsValue,
                        })
                      }
                      disabled={isCardDisabled}
                      price={currentPrice}
                    />
                  );
                }
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldValue, setFieldsValue }) => {
        const rows = organizeColumns(groupedOptions);

        return (
          <div className="space-y-3">
            {/* Row 1: Options - full width with grid */}
            {rows.row1.length > 0 && (
              <div className="w-full">
                {renderColumn(rows.row1, setFieldsValue)}
              </div>
            )}

            {/* Row 2: Devices - full width */}
            {rows.rowDevice.length > 0 && (
              <div className="w-full">
                {renderColumn(rows.rowDevice, setFieldsValue)}
              </div>
            )}

            {/* Row 3: Video Integration
                - If Mobile group exists, keep them side-by-side.
                - If Mobile group is empty (our current requirement), keep Video full-width
                  so card widths match Options/Device cards. */}
            {rows.rowVideo.length > 0 || rows.rowMobile.length > 0 ? (
              rows.rowVideo.length > 0 && rows.rowMobile.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div>{renderColumn(rows.rowVideo, setFieldsValue)}</div>
                  <div>{renderColumn(rows.rowMobile, setFieldsValue)}</div>
                </div>
              ) : rows.rowVideo.length > 0 ? (
                <div className="w-full">
                  {renderColumn(rows.rowVideo, setFieldsValue)}
                </div>
              ) : (
                <div className="w-full">
                  {renderColumn(rows.rowMobile, setFieldsValue)}
                </div>
              )
            ) : null}
          </div>
        );
      }}
    </Form.Item>
  );
};

export default OptionCards;
